import {
  createContext,
  createElement,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';
import { ItemIdentifier, LocationIdentifier } from '@sas/nova-core/services/drive';

export interface ContentNavigationContextValue {
  /**
   * The current location being used by consumers of this provider
   */
  location?: ItemIdentifier;
  /**
   * The current location path used by consumers that display a hierarchy, such as tree and breadcrumb
   */
  locationContextPath: ItemIdentifier[];
  /**
   * The location roots used by consumers of this provider, such as tree. Other components under the
   * provider will need to know of these location roots to calculate the correct locationContextPath.
   */
  locations?: LocationIdentifier[];
}
export const ContentNavigationStateContext = createContext<
  ContentNavigationContextValue | undefined
>(undefined);
export const ContentNavigationStateDispatch = createContext<
  Dispatch<SetStateAction<ContentNavigationContextValue>> | undefined
>(undefined);

/**
 * Returns the current navigation context for the nearest provider, or undefined if not under a
 * ContentNavigationProvider.
 */
export const useContentNavigationState = () => {
  return useContext(ContentNavigationStateContext);
};

/**
 * Dispatch a new navigation context to all components consuming this provider. Will return undefined
 * if not under a ContentNavigationProvider.
 */
export const useContentNavigationDispatch = () => {
  return useContext(ContentNavigationStateDispatch);
};

export interface ContentNavigationProviderProps {
  children?: ReactNode;
  initialValue: ContentNavigationContextValue;
}

/**
 * Provide navigational behavior to components that consume this provider. This means all consumers
 * will display the same location (generally a folder), whether it's the contents of the folder, or
 * the folder itself, depending on the component. Components can consume and dispatch changes to
 * the current navigation using the hooks.
 * @param initialValue
 * @param children
 * @constructor
 */
export const ContentNavigationProvider = ({
  initialValue,
  children,
}: ContentNavigationProviderProps) => {
  const [state, setState] = useState<ContentNavigationContextValue>(initialValue);
  return (
    <ContentNavigationStateContext.Provider value={state}>
      <ContentNavigationStateDispatch.Provider value={setState}>
        {children}
      </ContentNavigationStateDispatch.Provider>
    </ContentNavigationStateContext.Provider>
  );
};
